/* ============================================================================
   Theme Enhancements
   ============================================================================
   Global enhancements for improved UX and micro-interactions
   ========================================================================== */

class ThemeEnhancements {
  constructor() {
    this.init();
  }

  init() {
    this.enhanceAddToCartButtons();
    this.enhanceQuantityInputs();
    this.addCartBadgeAnimation();
    this.addPriceUpdateAnimation();
    this.addInventoryStatusIndicators();
    this.addVariantSelectionFeedback();
  }

  // Enhanced Add to Cart buttons with success feedback
  enhanceAddToCartButtons() {
    document.querySelectorAll('button[name="add"], .product-form__submit').forEach(button => {
      button.addEventListener('click', (e) => {
        if (button.hasAttribute('disabled')) return;

        // Add loading state
        button.classList.add('loading');

        // Listen for cart update
        const cartUpdateHandler = () => {
          button.classList.remove('loading');
          button.classList.add('added');

          // Revert after 2 seconds
          setTimeout(() => {
            button.classList.remove('added');
          }, 2000);

          document.removeEventListener('cart:updated', cartUpdateHandler);
        };

        document.addEventListener('cart:updated', cartUpdateHandler);

        // Fallback timeout
        setTimeout(() => {
          if (button.classList.contains('loading')) {
            button.classList.remove('loading');
          }
        }, 5000);
      });
    });
  }

  // Enhanced quantity inputs with validation
  enhanceQuantityInputs() {
    document.querySelectorAll('.quantity-input').forEach(container => {
      const input = container.querySelector('input[type="number"]');
      const decreaseBtn = container.querySelector('[name="minus"]');
      const increaseBtn = container.querySelector('[name="plus"]');

      if (!input) return;

      const updateButtons = () => {
        const value = parseInt(input.value) || 1;
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 99;

        if (decreaseBtn) {
          decreaseBtn.disabled = value <= min;
        }
        if (increaseBtn) {
          increaseBtn.disabled = value >= max;
        }
      };

      // Initial state
      updateButtons();

      // Update on change
      input.addEventListener('change', updateButtons);
      input.addEventListener('input', updateButtons);

      // Button click handlers
      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          const current = parseInt(input.value) || 1;
          const min = parseInt(input.min) || 1;
          if (current > min) {
            input.value = current - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => {
          const current = parseInt(input.value) || 1;
          const max = parseInt(input.max) || 99;
          if (current < max) {
            input.value = current + 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });
  }

  // Animate cart badge on update
  addCartBadgeAnimation() {
    const cartBadge = document.querySelector('.cart-count-bubble');
    if (!cartBadge) return;

    // Watch for cart updates
    document.addEventListener('cart:updated', () => {
      cartBadge.style.animation = 'none';
      setTimeout(() => {
        cartBadge.style.animation = '';
      }, 10);
    });

    // Observe text changes
    const observer = new MutationObserver(() => {
      cartBadge.style.animation = 'none';
      setTimeout(() => {
        cartBadge.style.animation = '';
      }, 10);
    });

    observer.observe(cartBadge, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  // Animate price updates on variant change
  addPriceUpdateAnimation() {
    document.querySelectorAll('.price').forEach(priceElement => {
      const observer = new MutationObserver(() => {
        priceElement.classList.add('updating');
        setTimeout(() => {
          priceElement.classList.remove('updating');
        }, 600);
      });

      observer.observe(priceElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
    });
  }

  // Add inventory status indicators
  addInventoryStatusIndicators() {
    // This would typically be called when variants change
    // Add to your existing variant change handlers
    document.addEventListener('variant:change', (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;

      const container = document.querySelector('.product-form__inventory');
      if (!container) return;

      let statusHTML = '';
      const inventory = variant.inventory_quantity || 0;
      const available = variant.available;

      if (!available) {
        statusHTML = `
          <div class="inventory-status inventory-status--out-of-stock">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L1 8l7 7 7-7-7-7z" stroke="currentColor" stroke-width="2"/>
            </svg>
            Out of stock
          </div>
        `;
      } else if (inventory > 0 && inventory < 10) {
        statusHTML = `
          <div class="inventory-status inventory-status--low-stock">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="2"/>
              <path d="M8 4v5M8 11h.01" stroke="currentColor" stroke-width="2"/>
            </svg>
            Only ${inventory} left in stock
          </div>
        `;
      } else if (available) {
        statusHTML = `
          <div class="inventory-status inventory-status--in-stock">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13 4L6 11 3 8" stroke="currentColor" stroke-width="2"/>
            </svg>
            In stock
          </div>
        `;
      }

      container.innerHTML = statusHTML;
    });
  }

  // Add variant selection feedback
  addVariantSelectionFeedback() {
    document.querySelectorAll('.product-form__input input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        // Visual feedback on selection
        const label = radio.nextElementSibling;
        if (label) {
          label.style.animation = 'none';
          setTimeout(() => {
            label.style.animation = '';
          }, 10);
        }

        // Haptic feedback on supported devices
        if ('vibrate' in navigator) {
          navigator.vibrate(10);
        }
      });
    });
  }
}

// Initialize enhancements
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ThemeEnhancements();
  });
} else {
  new ThemeEnhancements();
}

// Utility: Debounce function for performance
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Utility: Throttle function for scroll events
function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Export utilities for use in other scripts
window.themeUtils = {
  debounce,
  throttle
};

// Check for reduced motion preference
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Disable animations if reduced motion is preferred
if (prefersReducedMotion) {
  document.documentElement.classList.add('reduce-motion');
}
